import { useState, useEffect } from 'react';
import {
  CssBaseline, ThemeProvider, createTheme, AppBar, Toolbar,
  Typography, Button, Container, Box, CircularProgress,
} from '@mui/material';
import EmployeeView from './views/EmployeeView';
import AdminView from './views/AdminView';
import Footer from './components/Footer';
import { login, handleCallback, isAuthenticated, getUser, logout } from './auth';

const SYNERGY_LOGO = 'https://synergyecp.com/wp-content/uploads/2024/07/Synergy-ECP-Logo-with-tag-line-rebuild-Sep2021-simple-colors-text-converted-to-shapes-copy.svg';

const theme = createTheme({
  palette: {
    primary: { main: '#df4601' },
    secondary: { main: '#000000' },
    background: { default: '#fafafa' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
  },
});

const stitchPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23df4601' stroke-width='0.4' opacity='0.15'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3Cpath d='M22 30 Q30 22 38 30'/%3E%3Cpath d='M22 30 Q30 38 38 30'/%3E%3Cline x1='23' y1='26' x2='20' y2='23'/%3E%3Cline x1='37' y1='26' x2='40' y2='23'/%3E%3Cline x1='23' y1='34' x2='20' y2='37'/%3E%3Cline x1='37' y1='34' x2='40' y2='37'/%3E%3C/g%3E%3C/svg%3E")`;

export default function App() {
  const [authState, setAuthState] = useState('loading'); // loading | authed | unauthd
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState('employee');

  useEffect(() => {
    async function init() {
      // Handle redirect callback from Cognito
      if (window.location.search.includes('code=')) {
        const ok = await handleCallback();
        if (!ok) {
          setAuthState('unauthd');
          return;
        }
      }
      if (isAuthenticated()) {
        const u = getUser();
        setUser(u);
        setAuthState('authed');
        // Auto-set admin mode if user is in admin group
        if (u?.isAdmin) setMode('admin');
      } else {
        setAuthState('unauthd');
      }
    }
    init();
  }, []);

  // Redirect to Cognito login if not authenticated
  useEffect(() => {
    if (authState === 'unauthd') login();
  }, [authState]);

  if (authState === 'loading' || authState === 'unauthd') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          minHeight: '100vh',
          backgroundImage: stitchPattern,
          backgroundSize: '60px 60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        backgroundImage: stitchPattern,
        backgroundSize: '60px 60px',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <AppBar position="static" color="primary" elevation={2}>
          <Toolbar sx={{ minHeight: 56, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <img
                src={SYNERGY_LOGO}
                alt="Synergy ECP"
                style={{ height: 28, objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
              />
            </Box>
            <Box sx={{ width: '1px', height: 24, bgcolor: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
            <Typography
              variant="subtitle1"
              sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 0.3, whiteSpace: 'nowrap' }}
            >
              ⚾ Orioles Ticket Lottery
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.85, mr: 1 }}>
              {user?.name}
            </Typography>
            {user?.isAdmin && (
              mode === 'admin' ? (
                <Button color="inherit" size="small" onClick={() => setMode('employee')}>
                  Employee View
                </Button>
              ) : (
                <Button color="inherit" size="small" onClick={() => setMode('admin')}>
                  Admin
                </Button>
              )
            )}
            <Button color="inherit" size="small" onClick={logout}>
              Sign out
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
          <Box sx={{ py: 4 }}>
            {mode === 'employee' ? <EmployeeView employeeName={user?.name} /> : <AdminView />}
          </Box>
        </Container>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}
